import type { MRT_VisibilityState } from "material-react-table";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import {
  fromColumnVisibilityEntries,
  tableColumnPreferencesApi,
  useGetTableColumnPreferencesQuery,
  useSaveTableColumnVisibilityMutation,
} from "_shared/_views/tables/_api/table-column-preferences-api";
import { listViewPathOf } from "_shared/_views/list-pages/current-table-view";
import { openedViewIdOf, stillSavedViewId } from "_shared/_views/list-pages/use-current-table-view";
import { fromTableViewStateJSON, useSaveCurrentTableViewMutation } from "_shared/_views/tables/_api/table-views-api";
import {
  type ColumnLayout,
  type TableColumnPreferences,
  TableColumnPreferencesContext,
} from "_shared/_views/tables/table-column-preferences-context";
import { useSession } from "users/__session/session-context";

/** How old an answer may be and still serve a table that opens, rather than being asked for again. */
export const TABLE_PREFERENCES_FRESH_FOR_MS = 5_000;

/**
 * Hands every table below it the reader's column choices from the server, and saves new ones there,
 * so a choice follows the reader to another browser and survives signing out (#2788).
 *
 * One request answers for every table: the choices are small and a reader moves between tables, so
 * asking per table would cost a round trip on each page for no saving.
 */
export function TableColumnPreferencesProvider({ children }: { readonly children: ReactNode }) {
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const isLoggedIn = Boolean(session?.isLoggedIn);
  // Asked for again when the reader comes back to this tab or window, as well as when a table opens:
  // the provider stays mounted from page to page, and a choice reset in another browser would
  // otherwise never reach this one (#6151 review).
  const query = useGetTableColumnPreferencesQuery({ session }, { skip: !isLoggedIn, refetchOnFocus: true });
  const [save] = useSaveTableColumnVisibilityMutation();

  const savedTables = useMemo(() => {
    // A failed answer is no answer: the tables fall back to this browser's copy rather than to
    // nothing, which would put every hidden column back for a reader whose choice the server holds.
    if (!isLoggedIn || !query.data || query.isError) return undefined;
    return new Map(query.data.tables.map((table) => [table.tableKey, fromColumnVisibilityEntries(table.columns)]));
  }, [isLoggedIn, query.data, query.isError]);

  // The widths and order of each table's current view (#6714). A table with no current view keeps
  // none, and opens with the widths its fit gives it.
  const savedLayouts = useMemo(() => {
    if (!isLoggedIn || !query.data || query.isError) return undefined;
    return new Map(
      query.data.tables.flatMap((table) => {
        if (!table.currentView) return [];
        const { columnSizing, columnOrder } = fromTableViewStateJSON(table.currentView.state);
        return [[table.tableKey, { columnSizing, columnOrder } satisfies ColumnLayout] as const];
      }),
    );
  }, [isLoggedIn, query.data, query.isError]);

  const [saveCurrentView] = useSaveCurrentTableViewMutation();
  const tables = query.data?.tables;
  const saveLayout = useCallback(
    (tableKey: string, part: ColumnLayout) => {
      if (!session?.isLoggedIn) return;
      // The view the reader is looking at is the one at this location: a width is kept with the tab
      // and chips it was dragged in, as a chip or tab change is (`useCurrentTableView`).
      const view = listViewPathOf(globalThis.location.pathname, globalThis.location.search);
      const table = tables?.find((each) => each.tableKey === tableKey);
      const stored = table?.currentView?.viewId ?? null;
      // A view the page opened itself, such as the starred view of a bare visit, is the one in use
      // though nothing was saved for it yet; React Router keeps a history entry's state under `usr`.
      // One deleted since is the Default view, as `useCurrentTableView` keeps it.
      const opened = stillSavedViewId(
        openedViewIdOf((globalThis.history.state as { usr?: unknown } | null)?.usr),
        tables ? (table?.views ?? []) : undefined,
      );
      const viewId = opened === undefined ? stored : opened;
      saveCurrentView({ session, tableKey, viewId, state: { tabPath: view.tabPath, search: view.search, ...part } })
        .unwrap()
        .catch((error: unknown) => {
          // The table still shows the reader's widths; they simply do not follow the reader elsewhere.
          console.warn(`Could not keep the column layout of the ${tableKey} table.`, error);
        });
    },
    [saveCurrentView, session, tables],
  );

  const saveTable = useCallback(
    (tableKey: string, visibility: MRT_VisibilityState) => {
      if (!session?.isLoggedIn) return;
      save({ session, tableKey, visibility })
        .unwrap()
        .catch((error: unknown) => {
          // The column is still hidden in this browser, and still remembered here; it simply does
          // not follow the reader elsewhere. Nothing for them to do, so no snackbar.
          console.warn(`Could not save the columns chosen for the ${tableKey} table.`, error);
        });
    },
    [save, session],
  );

  const { fulfilledTimeStamp, isUninitialized } = query;
  const refresh = useCallback(() => {
    if (!isLoggedIn || isUninitialized) return;
    if (fulfilledTimeStamp !== undefined && Date.now() - fulfilledTimeStamp < TABLE_PREFERENCES_FRESH_FOR_MS) return;
    // Child mount effects run before this provider's subscription effect. A cached answer can
    // already be initialized while the hook has no subscription to refetch yet (#6157).
    void dispatch(tableColumnPreferencesApi.endpoints.getTableColumnPreferences.initiate(
      { session }, { forceRefetch: true, subscribe: false },
    ));
  }, [dispatch, fulfilledTimeStamp, isLoggedIn, isUninitialized, session]);

  const [viewRevisions, setViewRevisions] = useState<ReadonlyMap<string, number>>(() => new Map());
  const openedView = useCallback((tableKey: string) => {
    setViewRevisions((previous) => new Map(previous).set(tableKey, (previous.get(tableKey) ?? 0) + 1));
  }, []);

  const value = useMemo<TableColumnPreferences>(
    () => ({ savedTables, saveTable, refresh, savedLayouts, saveLayout, viewRevisions, openedView }),
    [savedTables, saveTable, refresh, savedLayouts, saveLayout, viewRevisions, openedView],
  );

  return <TableColumnPreferencesContext.Provider value={value}>{children}</TableColumnPreferencesContext.Provider>;
}
